import { Controller } from "@hotwired/stimulus"
import tippy from 'tippy.js'

// Connects to data-controller="turbo-modal"
export default class extends Controller {
  static targets = ["content", "expandButton"]
  static values = {
    undismissable: Boolean,
    outsideClickClose: Boolean
  }

  static EXPANDED_STORAGE_KEY = "turbo-sidepanel-expanded"

  get expandedIcon() {
    return "<i class='far fa-arrow-down-left-and-arrow-up-right-to-center'></i>"
  }

  get collapsedIcon() {
    return "<i class='far fa-arrow-up-right-and-arrow-down-left-from-center'></i>"
  }

  connect() {
    this.isExpanded = false
    this.isSidepanel = this.contentTarget.classList.contains('manage_modal--sidepanel')
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleMouseDown = this.handleMouseDown.bind(this)
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    this.boundHandleFrameRender = this.handleFrameRender.bind(this)

    document.addEventListener('keydown', this.boundHandleKeydown)
    this.element.addEventListener('mousedown', this.boundHandleMouseDown)
    this.element.addEventListener('click', this.boundHandleOutsideClick)
    this.element.addEventListener('turbo:frame-render', this.boundHandleFrameRender)

    // Only trigger slide-in animation for sidepanel modals
    if (this.isSidepanel) {
      // Restore the expand/collapse state persisted across page loads
      this.applyExpandedState(this.readPersistedExpanded())
      requestAnimationFrame(() => {
        this.element.classList.add('is-visible')
      })
    }

    this.initializeTippy()

    if (this.isSidepanel) {
      return
    }

    if (this.isDisplayed()) {
      this.activateOverlay()
    } else {
      this.watchUntilDisplayed()
    }
  }
  
  initializeTippy() {
    // Initialize tippy tooltips for modal content
    if (window.initializeTippy) {
      window.initializeTippy(this.element);
    } else {
      // Fallback to direct initialization if shared function isn't available
      tippy('[data-tippy-content]', {
        appendTo: () => this.element,
        allowHTML: true
      });
    }

    if (window.initializeOverflowTooltips) {
      window.initializeOverflowTooltips(this.element);
    }
  }
  
  handleFrameRender() {
    // Re-initialize tippy when frame content is updated (e.g., prev/next navigation)
    this.initializeTippy()
  }

  disconnect() {
    this.visibilityObserver?.disconnect()
    this.visibilityObserver = null

    document.removeEventListener('keydown', this.boundHandleKeydown)
    this.element.removeEventListener('mousedown', this.boundHandleMouseDown)
    this.element.removeEventListener('click', this.boundHandleOutsideClick)
    this.element.removeEventListener('turbo:frame-render', this.boundHandleFrameRender)

    if (!this.isSidepanel && this.overlayActivated) {
      this.unlockScroll()
    }

    this.overlayActivated = false
    this.restoreFocus()
  }

  handleMouseDown(event) {
    // Only track mouse position if modal is dismissable
    if (!this.undismissableValue) {
      this.mouseDownInsideModal = this.contentTarget.contains(event.target)
    }
  }

  handleOutsideClick(event) {
    // Only close if not undismissable and outsideClickCloseValue is true and both mousedown and click were outside the modal
    if (!this.undismissableValue &&
        this.outsideClickCloseValue &&
        !this.mouseDownInsideModal &&
        !this.contentTarget.contains(event.target) &&
        event.target === this.element) {
      this.hideModal()
    }
  }

  handleKeydown(event) {
    if (!this.element.isConnected) return
    if (!this.isSidepanel && !this.isDisplayed()) return

    // Only handle Escape if not undismissable
    if (event.key === "Escape" && !this.undismissableValue) {
      this.hideModal()
      return
    }

    if (event.key === "Tab" && !this.isSidepanel) {
      this.trapFocus(event)
    }
  }

  // Overlay modals sit in a Turbo Frame at the end of the document, so Tab from
  // the opener walks the rest of the page instead of the dialog. Move focus in
  // on connect and keep Tab cycling inside the panel (same pattern as the
  // filter bar and Hudini dock). Sidepanels are not fully modal, so they skip this.
  setupFocusTrap() {
    if (this.isSidepanel) return

    const active = document.activeElement
    this.previouslyFocusedElement = (active && active !== document.body) ? active : null

    if (!this.contentTarget.hasAttribute("tabindex")) {
      this.contentTarget.setAttribute("tabindex", "-1")
    }

    requestAnimationFrame(() => this.focusInitialElement())
  }

  activateOverlay() {
    if (this.overlayActivated) return
    this.overlayActivated = true

    this.lockScroll()
    this.setupFocusTrap()
  }

  // Pin the body at the current scroll offset so the page does not jump to
  // the top. `scroll-lock--pinned` sets overflow: visible so a 100vh table
  // (Users, etc.) is not clipped at the bottom.
  lockScroll() {
    this.scrollPosition = window.scrollY
    document.body.classList.add("scroll-lock")

    if (this.scrollPosition > 0) {
      document.body.style.top = `-${this.scrollPosition}px`
      document.body.classList.add("scroll-lock--pinned")
    }
  }

  unlockScroll() {
    document.body.classList.remove("scroll-lock", "scroll-lock--pinned")
    document.body.style.top = ""
    window.scrollTo(0, this.scrollPosition || 0)
  }

  watchUntilDisplayed() {
    this.visibilityObserver = new MutationObserver(() => {
      if (!this.isDisplayed()) return

      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
      this.activateOverlay()
    })
    this.visibilityObserver.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class", "style", "hidden"],
    })
  }

  isDisplayed() {
    let node = this.element
    while (node && node.nodeType === 1) {
      if (node.hidden || node.classList.contains("hidden")) return false
      if (node.style && (node.style.display === "none" || node.style.visibility === "hidden")) return false
      node = node.parentElement
    }
    return this.element.getClientRects().length > 0
  }

  focusInitialElement() {
    if (!this.hasContentTarget || !this.isDisplayed()) return
    if (this.contentTarget.contains(document.activeElement)) return

    const autofocus = this.contentTarget.querySelector("[autofocus]")
    if (autofocus) {
      this.focusWithoutTooltip(autofocus)
      return
    }

    // Focus the dialog, not the first control. Icon buttons use Tippy on
    // focus, so landing on Close would show "Close" with no hover.
    this.focusWithoutTooltip(this.contentTarget)
  }

  trapFocus(event) {
    if (!this.hasContentTarget) return

    const focusable = this.focusableElements()
    if (!this.contentTarget.contains(document.activeElement)) {
      event.preventDefault()
      if (focusable.length === 0) {
        this.contentTarget.focus()
        return
      }
      const wrapTarget = event.shiftKey ? focusable[focusable.length - 1] : focusable[0]
      wrapTarget.focus()
      return
    }

    if (focusable.length === 0) {
      event.preventDefault()
      this.contentTarget.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    // Initial focus lands on the tabindex="-1" panel, which is not in the
    // sequential tab order. Shift+Tab from there would leave the dialog.
    const atStart = document.activeElement === first || document.activeElement === this.contentTarget

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    } else if (event.shiftKey && atStart) {
      event.preventDefault()
      last.focus()
    }
  }

  focusableElements() {
    if (!this.hasContentTarget) return []

    const selector = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    return Array.from(this.contentTarget.querySelectorAll(selector)).filter(
      (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1 && el.getClientRects().length > 0
    )
  }

  focusFirstFocusable() {
    const focusable = this.focusableElements()
    if (focusable.length === 0) return false

    this.focusWithoutTooltip(focusable[0])
    return true
  }

  restoreFocus() {
    if (this.previouslyFocusedElement?.isConnected) {
      this.focusWithoutTooltip(this.previouslyFocusedElement)
    }
    this.previouslyFocusedElement = null
  }

  // Programmatic focus would otherwise open Tippy (default trigger includes
  // focus), e.g. the floating Archive button after the confirm modal closes.
  focusWithoutTooltip(element) {
    element.focus({ preventScroll: true })
    element._tippy?.hide()
  }

  cleanup() {
    // Only sidepanel modals live in the shared `#sidepanel` frame, so only they
    // should clear it on close. Full-screen modals (rendered in the `#modal`
    // frame) must not touch `#sidepanel` — otherwise closing a modal opened from
    // within a sidepanel (e.g. the Hudini "New Skill" modal) wipes the panel.
    if (!this.isSidepanel) return

    const frame = document.getElementById('sidepanel')
    if (frame) {
      frame.innerHTML = ''
      frame.removeAttribute("src")
    }
  }

  hideModal() {
    if (this.isSidepanel) {
      // Add animation before removing
      this.element.classList.remove('is-visible')

      // Wait for animation to complete before cleaning up
      this.element.addEventListener('transitionend', () => {
        this.cleanup()
        if (this.element.parentElement) {
          this.element.parentElement.removeAttribute("src")
          this.element.remove()
        }
      }, { once: true })
      return
    }

    if (this.contentTarget.classList.contains('manage_modal--lightbox')) {
      // For lightbox modals, check if opened via full page navigation (URL is /photos/...)
      // If so, go back. Otherwise just remove the modal overlay.
      if (window.location.pathname.startsWith('/photos/')) {
        window.history.back()
      } else {
        this.restoreFocus()
        this.cleanup()
        this.dismissElement()
      }
    } else {
      // For other modals, remove immediately
      this.restoreFocus()
      this.cleanup()
      this.dismissElement()
    }
  }

  // Removes the modal and any nested `turbo-frame` wrappers it leaves empty.
  // Modal views wrap TurboModalComponent (which renders its own
  // `turbo_frame_tag "modal"`) in another `turbo_frame_tag "modal"`, so the
  // loaded DOM nests two frames; removing only the modal element left an empty
  // inner frame (and a stale `src`) behind, which anything watching the
  // `#modal` frame for "a modal is open" — e.g. the Hudini dock's overlay
  // yield — read as still open, keeping the dock hidden after close.
  dismissElement() {
    let frame = this.element.parentElement
    this.element.remove()

    while (frame && frame.tagName === "TURBO-FRAME") {
      const parent = frame.parentElement
      frame.removeAttribute("src")
      if (!parent || parent.tagName !== "TURBO-FRAME" || frame.childElementCount > 0) break
      frame.remove()
      frame = parent
    }
  }

  expandModal(event) {
    event.preventDefault()
    this.applyExpandedState(!this.isExpanded)
    this.persistExpanded(this.isExpanded)
  }

  applyExpandedState(expanded) {
    this.isExpanded = expanded

    if (expanded) {
      this.contentTarget.classList.add('manage_modal--expanded')
    } else {
      this.contentTarget.classList.remove('manage_modal--expanded')
    }

    if (this.hasExpandButtonTarget) {
      this.expandButtonTarget.innerHTML = expanded ? this.expandedIcon : this.collapsedIcon
    }
  }

  readPersistedExpanded() {
    try {
      return window.localStorage.getItem(this.constructor.EXPANDED_STORAGE_KEY) === "true"
    } catch (e) {
      return false
    }
  }

  persistExpanded(expanded) {
    try {
      window.localStorage.setItem(this.constructor.EXPANDED_STORAGE_KEY, expanded ? "true" : "false")
    } catch (e) {
      // Ignore storage failures (e.g. private mode, disabled storage)
    }

    if (window.initializeOverflowTooltips) {
      window.initializeOverflowTooltips(this.element)
    }
  }

  submit(event) {
    const form = event.currentTarget.closest('form');

    if (form) {
      form.submit();
    }
  }

  downloadFileAndClose(event) {
    event.preventDefault()
    const form = event.currentTarget.closest('form')
    if (!form) return
  
    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    const url = `${form.action}?${params.toString()}`
    
    // Create a temporary link and click it
    const link = document.createElement('a')
    link.href = url
    link.dataset.turbo = 'false'
    document.body.appendChild(link)
    link.click()
    link.remove()
    
    this.hideModal()
  }
}
